import type { PropsWithChildren } from "react";
import { Head } from "@inertiajs/react";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { LibraryIcon, BookIcon, HouseIcon, BookUserIcon } from "lucide-react";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";

import usePage from "@/hooks/usePage";
import { usePublicationSubdomain } from "@/hooks/usePublicationSubdomain";
import { NavLink } from "@/components/NavLink/NavLink";
import { TopBar } from "@/components/TopBar/TopBar";

import "overlayscrollbars/overlayscrollbars.css";

export function AdminLayout({ children }: PropsWithChildren) {
  const { props } = usePage<{ user: any } & AdminUsePageProps>();
  const subdomain = usePublicationSubdomain();

  if (props.flash?.notice) {
    toast.success(props.flash.notice, {
      duration: 5000,
    });
  }

  return (
    <>
      <Head>
        <title>{props.publication.name}</title>
      </Head>
      <Toaster closeButton />
      <div className="w-full h-screen grid grid-cols-[240px_1fr] grid-rows-[min-content_1fr] bg-gray-100">
        <TopBar />
        <aside className="col-span-1 flex flex-col gap-4 px-3 text-sm font-medium">
          <nav className="flex flex-col gap-2 mt-3">
            <ul className="flex flex-col items-center gap-0.5">
              <li className="w-full">
                <NavLink href={subdomain}>
                  <HouseIcon size={15} strokeWidth={2.5} /> Home
                </NavLink>
              </li>
              <li className="w-full">
                <NavLink exact={false} href={`${subdomain}/content`}>
                  <BookIcon size={15} strokeWidth={2.5} /> Culture
                </NavLink>
              </li>
              <li className="w-full">
                <NavLink href={`${subdomain}/audience`}>
                  <BookUserIcon size={15} strokeWidth={2.5} /> Audience
                </NavLink>
              </li>
              <li className="w-full h-0.5 bg-gray-200 my-2" />
              <li className="w-full">
                <NavLink href={`${subdomain}/publication`}>
                  <LibraryIcon size={15} strokeWidth={2.5} /> Publication
                </NavLink>
              </li>
            </ul>
          </nav>
        </aside>
        <div className="border mr-2 mb-2 rounded-lg col-span-1 bg-white overflow-scroll">
          <OverlayScrollbarsComponent
            defer
            options={{
              scrollbars: { autoHide: "scroll", theme: "admin-area-scrollbar" },
            }}
            className="overflow-hidden h-full"
          >
            {children}
          </OverlayScrollbarsComponent>
        </div>
      </div>
    </>
  );
}
